/** How long a saved training-video position stays valid. */
export const VIDEO_RESUME_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const VIDEO_RESUME_VERSION = 1;
const KEY_PREFIX = "ld_video_resume";

function storage() {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch (e) {
    return null;
  }
}

/**
 * One record per learner + package + guidance, so a shared browser does not
 * resume someone else's training video.
 */
export function videoResumeStorageKey({ packageId, guidanceId, learnerId } = {}) {
  if (!packageId || !guidanceId) {
    return null;
  }

  return [KEY_PREFIX, packageId, guidanceId, learnerId || "anon"].join(":");
}

function readSavedProgress(data) {
  if (!data || typeof data !== "object" || data.v !== VIDEO_RESUME_VERSION) {
    return null;
  }

  const savedAt = data.savedAt ? new Date(data.savedAt).getTime() : NaN;
  if (!Number.isFinite(savedAt) || Date.now() - savedAt > VIDEO_RESUME_TTL_MS) {
    return null;
  }

  const currentTime = Number(data.currentTime);
  const maxWatched = Number(data.maxWatched);
  const duration = Number(data.duration);

  if (!Number.isFinite(currentTime) || currentTime < 0) {
    return null;
  }
  if (!Number.isFinite(maxWatched) || maxWatched < 0) {
    return null;
  }

  const safeDuration = Number.isFinite(duration) && duration > 0 ? duration : 0;
  const safeMax = safeDuration > 0 ? Math.min(maxWatched, safeDuration) : maxWatched;
  const safeTime = Math.min(currentTime, safeMax > 0 ? safeMax : currentTime);

  if (safeTime <= 0 && safeMax <= 0) {
    return null;
  }

  return {
    currentTime: safeTime,
    maxWatched: Math.max(safeMax, safeTime),
    duration: safeDuration,
  };
}

/** Saved playback position, or null when missing, expired, or unreadable. */
export function readVideoResume(key) {
  const ls = storage();
  if (!ls || !key) {
    return null;
  }

  try {
    const raw = ls.getItem(key);
    if (!raw) {
      return null;
    }

    const saved = readSavedProgress(JSON.parse(raw));
    if (!saved) {
      ls.removeItem(key);
      return null;
    }

    return saved;
  } catch (e) {
    return null;
  }
}

export function writeVideoResume(key, progress) {
  const ls = storage();
  if (!ls || !key || !progress) {
    return;
  }

  const currentTime = Number(progress.currentTime);
  const maxWatched = Number(progress.maxWatched);
  const duration = Number(progress.duration);

  if (!Number.isFinite(currentTime) || !Number.isFinite(maxWatched)) {
    return;
  }

  if (currentTime <= 0 && maxWatched <= 0) {
    return;
  }

  try {
    ls.setItem(
      key,
      JSON.stringify({
        v: VIDEO_RESUME_VERSION,
        currentTime,
        maxWatched,
        duration: Number.isFinite(duration) && duration > 0 ? duration : 0,
        savedAt: new Date().toISOString(),
      }),
    );
  } catch (e) {
    // Storage full or blocked: resume is a convenience, playback still works.
  }
}
